import {
  FILTERS_CONFIG,
  type TApiFilterKey,
  type TFilters,
  type TRuntimeFilterConfig,
  type TStoreFilterKey,
} from "./filters.config";

type TSearchParamValue = string | string[] | undefined;

export type TFilterSearchParams =
  | Pick<URLSearchParams, "getAll">
  | Record<string, TSearchParamValue>;

type TApiFilters<TFilterGroup extends keyof TFilters> = {
  [TFilter in keyof TFilters[TFilterGroup] as TFilter extends `${string}:${infer TKey}`
    ? TKey
    : never]: TFilters[TFilterGroup][TFilter];
};

const getSearchParamValues = (
  searchParams: TFilterSearchParams,
  filter: string,
): string[] => {
  if ("getAll" in searchParams && typeof searchParams.getAll === "function") {
    return searchParams.getAll(filter);
  }

  const value = (
    searchParams as Record<string, TSearchParamValue>
  )[filter];

  if (value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [ value ];
};

const getFilterConfigEntries = (
  filterGroup: keyof TFilters,
): Array<[ string, TRuntimeFilterConfig ]> => Object.entries(
  FILTERS_CONFIG[filterGroup],
) as unknown as Array<[ string, TRuntimeFilterConfig ]>;

const isDefaultValue = (value: unknown, defaultValue: unknown): boolean => {
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return value.length === defaultValue.length && value.every(
      (item, index) => item === defaultValue[index],
    );
  }

  return value === defaultValue;
};

const parseFilterValue = (
  values: string[],
  config: TRuntimeFilterConfig,
): unknown => {
  if (values.length === 0) {
    return config.defaultValue;
  }

  let value: unknown;

  if (Array.isArray(config.defaultValue)) {
    value = [
      ...new Set(
        values
          .flatMap((item) => item.split(","))
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    ];
  } else if (typeof config.defaultValue === "number") {
    value = Number(values[0]);
  } else if (typeof config.defaultValue === "boolean") {
    if (values[0] === "true") {
      value = true;
    } else if (values[0] === "false") {
      value = false;
    } else {
      value = config.defaultValue;
    }
  } else {
    value = values[0];
  }
  const result = config.schema.safeParse(value);

  return result.success ? result.data : config.defaultValue;
};

const serializeFilterValue = (value: unknown): string[] => Array.isArray(value)
  ? value.map(String)
  : [ String(value) ];

const updateSearchParam = (
  searchParams: URLSearchParams,
  filter: string,
  value: unknown,
  config: TRuntimeFilterConfig,
): void => {
  searchParams.delete(filter);

  const serializedValues = isDefaultValue(value, config.defaultValue)
    ? []
    : serializeFilterValue(value);

  for (const serializedValue of serializedValues) {
    searchParams.append(filter, serializedValue);
  }
};

export const parseFilters = (
  searchParams: TFilterSearchParams,
): TFilters => Object.fromEntries(
  (Object.keys(FILTERS_CONFIG) as Array<keyof TFilters>).map(
    (filterGroup) => [
      filterGroup,
      Object.fromEntries(
        getFilterConfigEntries(filterGroup).map(([ filter, config ]) => [
          filter,
          parseFilterValue(getSearchParamValues(searchParams, filter), config),
        ]),
      ),
    ],
  ),
) as TFilters;

export const updateSearchParams = (
  searchParams: URLSearchParams,
  filters: TFilters,
): void => {
  for (const filterGroup of Object.keys(FILTERS_CONFIG) as Array<keyof TFilters>) {
    for (const [ filter, config ] of getFilterConfigEntries(filterGroup)) {
      const value = filters[filterGroup][filter as keyof TFilters[typeof filterGroup]];

      updateSearchParam(searchParams, filter, value, config);
    }
  }
};

export const getApiFilterKey = <TFilter extends TStoreFilterKey>(
  filter: TFilter,
): TApiFilterKey<TFilter> => {
  const KEY_SLICE_INDEX = 1;
  return filter.slice(
    filter.indexOf(":") + KEY_SLICE_INDEX,
  ) as TApiFilterKey<TFilter>;
};

export const getApiFilters = <TFilterGroup extends keyof TFilters>(
  searchParams: TFilterSearchParams,
  filterGroup: TFilterGroup,
): TApiFilters<TFilterGroup> => Object.fromEntries(
  Object.entries(parseFilters(searchParams)[filterGroup]).map(
    ([ filter, value ]) => [
      getApiFilterKey(filter as TStoreFilterKey),
      value,
    ],
  ),
) as TApiFilters<TFilterGroup>;
